import React, { useState, useEffect } from 'react';
import { IconButton, Tooltip, Box } from "@mui/material";
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import DeleteIcon from "@mui/icons-material/Delete";
import { Artist, CreateArtistRequest, Country, AssignArtistRequest, ArtistSource } from '../../models/artist';
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Alert, Snackbar } from "@mui/material";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    Typography,
} from "@mui/material";

//Error Handling
import { useErrorHandler, isNullOrEmpty } from '../../hooks/useErrorHandler';
import ErrorSummary from '../ErrorHandler/ErrorSummary';

interface Props {
    open: boolean;
    onClose: () => void;
    onUpdate: (artist: Artist) => Promise<void>;
    mode: "view" | "edit" | "assign" | "create";
    artist?: Artist | null;
    countries: Country[];
    existingArtists: Artist[];
}

const ArtistAssignment: React.FC<Props> = ({ open, onClose, onUpdate, mode, artist, countries, existingArtists }) => {

    const { errors, handleError, hasErrors, clearErrors, validateRequired } = useErrorHandler();

    const [formValues, setFormValues] = useState<Artist>(
        artist || { artistId: 0, artistName: "", countryCode: "", source: ArtistSource.User, biography:"", clientBio: "" }
    );

    useEffect(() => {
        clearErrors();

        if (artist) {
            setFormValues(artist);
            setBirthDate(artist?.birthDate ? dayjs(artist.birthDate) : null);
            setDeathDate(artist?.deathDate ? dayjs(artist.deathDate) : null);
        } else {
            const newArtist: Artist = {
                artistId:0,
                artistName:"",
                countryCode: "",
                artistPeriod: "",
                source: ArtistSource.User
            };

            setFormValues(newArtist);
            setBirthDate(null);
            setDeathDate(null);
        }

    }, [artist, open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const readOnly = mode === "view";

    const [birthDate, setBirthDate] = useState<Dayjs | null>(null);
    const [deathDate, setDeathDate] = useState<Dayjs | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateDates = (birthValue?: any, deathValue?: any) => {
        const birth = birthValue ? birthValue : null;
        const death = deathValue ? deathValue : null;

        const errors = { birth: "", death: "" };

        if (birth && !birth.isValid()) handleError({ code: 'Date', message: 'Invalid birth date', details: '' });
        if (death && !death.isValid()) handleError({ code: 'Date', message: 'Invalid death date', details: '' });
        if (birth && death && !death.isAfter(birth)) {
            handleError({ code: 'Date', message: 'Invalid birth and death dates.  Birth date must be before death date.', details: '' });
        }
        return !errors.birth && !errors.death;
    };

    const validateForm = (): boolean => {

        //Validate Form
        clearErrors();
        let isValid = true;

        //if (isNullOrEmpty(formValues.artistName)) handleError({ code: 'Artists', message: 'Artist name is required', details: '' });
        if(!validateRequired(formValues.artistName, 'Artist Name', 'Artist name is required.')) isValid=false;

        if (!isNullOrEmpty(formValues.artistName) && mode === "create") {
            const isDuplicate = existingArtists.some(
                artist => artist.artistName.toLowerCase() === formValues.artistName.toLowerCase());
            if (isDuplicate) {
                handleError({ code: 'Artist', message: 'Artist already exists', details: 'Please search for the artists and update.' });
                isValid = false;
            }
        }

        //validateDates(birthDate, deathDate);

        //if (isNullOrEmpty(formValues.countryCode)) handleError({ code: 'Country', message: 'Country is required', details: 'Please select a country.' });
        if (!validateRequired(formValues.countryCode, 'Country', 'Please select a country.')) isValid=false;

        const bio = formValues.source == ArtistSource.User ? formValues.biography : formValues.clientBio;

        //if (!validateRequired(bio, 'Biography', 'An artist biography is required.')) isValid=false;


        return isValid;
    };

    const handleUpdate = async () => {

        if (!validateForm()) return;

        formValues.birthDate = birthDate ? birthDate.format("YYYY-MM-DD") : null;

        formValues.deathDate = deathDate ? deathDate.format("YYYY-MM-DD") : null;

        setIsSubmitting(true);

        try {
            await onUpdate(formValues);
            onClose();
        } catch (error) {
            console.error('Failed to update artist:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {mode === "view" && "Artist Details"}
                {mode === "edit" && (artist ? "Edit Artist" : "Add Artist")}
                {mode === "assign" && "Assign Artist"}
                {mode === "create" && "Add New Artist"}
            </DialogTitle>

            <DialogContent dividers>
                {/* Error Panel*/}
                <Box>
                    <ErrorSummary errors={errors} title="Artist Detail Errors" />
                </Box>
                {readOnly ? (
                    <>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {artist?.artistName}
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
                            {artist?.artistPeriod}
                        </Typography>
  
                        {artist?.countryName && (
                            <Typography sx={{ mt: 1 }}>
                                Country: {artist?.countryName}
                            </Typography>
                        )}
                        {artist?.biography && (
                            <Typography variant="body2" sx={{ mt: 2, whiteSpace: "pre-line" }}>
                                {artist?.biography && (
                                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                        {artist.biography}
                                    </Typography>
                                )}

                                {artist?.clientBio && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: artist.biography ? 1 : 0,
                                            fontStyle: "italic",
                                            color: "text.secondary",
                                            whiteSpace: "pre-line",
                                        }}
                                    >
                                        — {artist.clientBio}
                                    </Typography>
                                )}

                            </Typography>
                        )}
                    </>
                ) : (
                        <Grid container spacing={2}>
                            {formValues.source == ArtistSource.System ? (
                                <>
                                <Grid size={{ xs: 12 }} >
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        {artist?.artistName}
                                    </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }} >
                                        <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
                                            {artist?.artistPeriod
                                                ? `Artist Dates: ${artist?.artistPeriod}`
                                                : ""}
                                        </Typography>
                                    </Grid>
                                    
                                    {artist?.countryName && (
                                        <Grid size={{ xs: 12 }} >
                                        <Typography sx={{ mt: 1 }}>
                                            Country: {artist?.countryName}
                                        </Typography>
                                        </Grid>
                                    )}
                                    </>
                                    ) : (
                                        <>
                                    <Grid size={{ xs: 12 }} >
                                        <TextField
                                            label="Artist Name"
                                            name="artistName"
                                            value={formValues.artistName || ""}
                                            onChange={handleChange}
                                            fullWidth
                                            required
                                        />
                                        </Grid>
                                        <Grid size={{ xs: 12 }} >
                                            <TextField
                                                label="Artist Dates"
                                                name="artistPeriod"
                                                value={formValues.artistPeriod || ""}
                                                onChange={handleChange}
                                                fullWidth
                                            />
                                        </Grid>

                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            select
                                            label="Country"
                                            name="countryCode"
                                            value={formValues.countryCode || ""}
                                            onChange={handleChange}
                                                fullWidth
                                                required
                                        >
                                            {countries.map((c) => (
                                                <MenuItem key={c.countryCode} value={c.countryCode}>
                                                    {c.countryName}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    
                                </>)}

                            {formValues.source == ArtistSource.User && (
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label="Biography"
                                        name="biography"
                                        value={formValues.biography || ""}
                                        onChange={handleChange}
                                        fullWidth
                                        multiline
                                        minRows={3}
                                    />
                                </Grid>
                            )}
                            {formValues.source == ArtistSource.System && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" sx={{ mt: 2, whiteSpace: "pre-line" }}>
                                    {artist?.biography && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 2,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                    backgroundColor: "background.paper",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontStyle: "italic",
                                                        color: "text.secondary",
                                                        whiteSpace: "pre-line",
                                                    }}
                                                >
                                                    <strong>Artist Bio:</strong> {artist.biography}
                                                </Typography>
                                            </Box>

                                    )}
                                    </Typography>
                                    <br></br>
                                    <TextField
                                        label="Client Biography"
                                        name="clientBio"
                                        value={formValues.clientBio || ""}
                                        onChange={handleChange}
                                        fullWidth
                                        multiline
                                        minRows={3}
                                    />
                                </Grid>
                            )}
                    </Grid>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {!readOnly && (
                    <Button variant="contained" onClick={handleUpdate}>
                        {mode === "assign" ? "Assign" : mode === "edit" ? "Update" : "Add"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

    export default ArtistAssignment;